"use strict";

export const maybe = (b) => (f) => (ma) => (ma == null ? b : f(ma));

/**
 * @param {any} a
 * @returns {string}
 */
export const get_type = (a) => {
	switch (true) {
		case a === null:
			return "Null";
		case a === undefined:
			return "Undefined";
		case Number.isNaN(a):
			return "NAN";
		default:
			return Object.prototype.toString.call(a).slice(8, -1);
	}
};

export const Stack = class {
	/**
	 * @template A
	 * @param {{ stack: Array<A>, item: A }} param0
	 */
	static push = ({ stack, item }) => [...stack, item];

	/**
	 * @template A
	 * @param {Array<A>} stack
	 * @returns {null | { popped: A, rest: Array<A> }}
	 */
	static pop = (stack) => {
		switch (stack.length) {
			case 0:
				return null;
			case 1:
				return { popped: stack[0], rest: [] };
			default:
				return { popped: stack[stack.length - 1], rest: stack.slice(0, stack.length - 1) };
		}
	};
};

/**
 * @template A
 */
export const ObjectDFSIterator = class {
	/**
	 * @param {{ frames: Array<Array<[string, A]>>, indices: Array<number>, lens: Array<string> }} param0
	 */
	constructor({ frames, indices, lens }) {
		this.frames = frames;
		this.indices = indices;
		this.lens = lens;
	}

	/**
	 * @template A
	 * @param {{ callback: (item: { lens: Array<string>, item: A }) => void, state: ObjectDFSIterator<A> }} param0
	 */
	static for_each = ({ callback, state }) => {
		for (let a = ObjectDFSIterator.next(state); a !== null; a = ObjectDFSIterator.next(a.next_state)) {
			callback(a.result);
		}
	};

	/**
	 * @template B
	 * @param {any} object
	 * @returns {ObjectDFSIterator<B>}
	 */
	static new = (object) =>
		((entries) =>
			entries.length === 0
				? new ObjectDFSIterator({
						frames: [],
						indices: [],
						lens: [],
					})
				: new ObjectDFSIterator({
						frames: [entries],
						indices: [0],
						lens: [],
					}))(Object.entries(object));

	/**
	 * @template A
	 * @param {ObjectDFSIterator<A>} state
	 * @returns {null | { result: { lens: Array<string>, item: A }, next_state: ObjectDFSIterator<A> }}
	 */
	static next = (state) => {
		let states = [state];
		for (let maybe_state = Stack.pop(states); maybe_state !== null; maybe_state = Stack.pop(states)) {
			const { popped: state, rest: states_rest } = maybe_state;
			states = states_rest;
			const maybe_frames = Stack.pop(state.frames),
				maybe_indices = Stack.pop(state.indices);
			if (maybe_frames !== null && maybe_indices !== null) {
				const { popped: frame, rest: frames_rest } = maybe_frames,
					{ popped: index, rest: indices_rest } = maybe_indices;
				if (index < frame.length) {
					const entry = frame[index];
					const lens = Stack.push({ stack: state.lens, item: entry[0] });
					if (get_type(entry[1]) === "Object") {
						states = Stack.push({
							stack: states_rest,
							item: new ObjectDFSIterator({
								frames: Stack.push({
									stack: state.frames,
									item: Object.entries(entry[1]),
								}),
								indices: Stack.push({
									stack: Stack.push({ stack: indices_rest, item: index + 1 }),
									item: 0,
								}),
								lens,
							}),
						});
					} else {
						return {
							result: { lens, item: entry[1] },
							next_state: new ObjectDFSIterator({
								frames: state.frames,
								indices: Stack.push({ stack: indices_rest, item: index + 1 }),
								lens: state.lens,
							}),
						};
					}
				} else {
					states = Stack.push({
						stack: states_rest,
						item: new ObjectDFSIterator({
							frames: frames_rest,
							indices: indices_rest,
							lens: ((maybe_lens) => (maybe_lens === null ? [] : maybe_lens.rest))(Stack.pop(state.lens)),
						}),
					});
				}
			}
		}
		return null;
	};
};

export const constrain = (minimum, maximum, value) => Math.min(Math.max(value, minimum), maximum);
